import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { api, type WeeklyReport, type WeeklyStorm } from '@/lib/api';
import { cn } from '@/lib/utils';
import { KIND_LABEL } from '@/lib/vsSeverity';
import { useAllianceLabel } from '@/lib/useCurrentAlliance';
import { downloadPoster, PosterCard, PosterFrame, PosterScaledPreview } from '@/components/Poster';
import { Loading, Panel, StatTile, fmtDate, fmtPct, playerLabel } from './shared';

// Plan 056 — weekly report: poster + chat text (≤ 500 characters).

const TEXT_LIMIT = 500;
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const UPKEEP_REMINDER = 'Keep your power and kills up to date in the Player Hub (at least every 2 weeks).';
const REGISTRATION_REMINDER = 'Register for CS by Monday 12:00 ST and for DS by Wednesday 12:00 ST!';

/** "2026-10-26" → "26.10." */
function dayMonth(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}.${m}.`;
}

function fmtBillions(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} B` : `${m.toFixed(0)} M`;
}

function fmtKills(m: number): string {
  return `${m.toFixed(m >= 10 ? 1 : 2)} M`;
}

/** `up: null` = neutral reference value, not a change. */
type Delta = { text: string; up: boolean | null };

function pctDelta(now: number | null | undefined, prev: number | null | undefined): Delta | null {
  if (now == null || prev == null || prev === 0) return null;
  const pct = ((now - prev) / prev) * 100;
  return { text: `${pct >= 0 ? '+' : ''}${pct.toFixed(0)} %`, up: pct >= 0 };
}

/** Rates (0..1) compared in percentage points. */
function ptsDelta(now: number | null | undefined, prev: number | null | undefined): Delta | null {
  if (now == null || prev == null) return null;
  const pts = Math.round((now - prev) * 100);
  return { text: `${pts >= 0 ? '+' : ''}${pts} pts`, up: pts >= 0 };
}

function countDelta(now: number, prev: number): Delta {
  return { text: `${now - prev >= 0 ? '+' : ''}${now - prev}`, up: now >= prev };
}

/** VS totals only compare as % between weeks of the same kind. */
function vsDelta(data: WeeklyReport): Delta | null {
  const { vsKind, vsTotalM } = data.previous;
  if (!data.vs || vsKind == null || vsTotalM == null) return null;
  if (vsKind === data.vs.kind) return pctDelta(data.vs.totalM, vsTotalM);
  return { text: `last week ${KIND_LABEL[vsKind]} ${fmtBillions(vsTotalM)}`, up: null };
}

function deltaSuffix(d: Delta | null): string {
  return d ? ` (${d.text} vs last week)` : '';
}

function stormLine(label: string, s: WeeklyStorm | null): string | null {
  if (!s || s.picked === 0) return null;
  const attended = s.showed + s.late;
  return `${label}: ${attended}/${s.picked} showed${s.attendanceRate != null ? ` (${fmtPct(s.attendanceRate)})` : ''}`;
}

/**
 * Chat text: fixed reminders always stay; optional lines are dropped from
 * the lowest priority up until the text fits.
 */
function buildText(data: WeeklyReport, alliance: string): string {
  type Line = { text: string; priority: number };
  const lines: Line[] = [];
  const range = `${dayMonth(data.weekStart)}–${dayMonth(data.weekEnd)}`;
  lines.push({ text: `${alliance ? `[${alliance}] ` : ''}Weekly report ${range}`, priority: Infinity });
  if (data.vs) {
    const outcome = data.vs.outcome === 'win' ? ' · won' : data.vs.outcome === 'loss' ? ' · lost' : '';
    lines.push({
      text: `VS (${KIND_LABEL[data.vs.kind]}) vs ${data.vs.opponentName} ${data.vs.opponentServer}: ${fmtBillions(data.vs.totalM)}${outcome}`,
      priority: 9,
    });
    if (data.vs.top.length > 0) {
      lines.push({
        text:
          data.vs.topMode === 'save'
            ? `Save masters (closest to 7.2 M/day Mon-Thu): ${data.vs.top.map((t) => playerLabel(t.player)).join(', ')}`
            : `VS top: ${data.vs.top.map((t) => `${playerLabel(t.player)} ${t.scoreM} M`).join(', ')}`,
        priority: 4,
      });
    }
  }
  if (data.kills.measured > 0) {
    const top = data.kills.top[0];
    lines.push({
      text: `Kills: +${fmtKills(data.kills.totalM)}${top ? ` · top: ${playerLabel(top.player)} +${fmtKills(top.deltaKillsM)}` : ''}`,
      priority: 8,
    });
  }
  const storms = [stormLine('CS', data.cs), stormLine('DS', data.ds)].filter(Boolean);
  if (storms.length > 0) lines.push({ text: storms.join(' · '), priority: 7 });
  if (data.perfect.count > 0) {
    lines.push({
      text: `Perfect week: ${data.perfect.count} players${deltaSuffix(countDelta(data.perfect.count, data.previous.perfect))}`,
      priority: 6,
    });
  }
  if (data.storms.uniqueRegistered > 0) {
    lines.push({
      text: `CS/DS: ${data.storms.uniqueRegistered} registered, ${data.storms.notPickedAtAll} not picked`,
      priority: 5,
    });
  }
  if (data.trains.regular + data.trains.megaExpress > 0) {
    lines.push({
      text: `Trains: ${data.trains.regular}${data.trains.megaExpress > 0 ? ` + ${data.trains.megaExpress} Mega Express` : ''}`,
      priority: 2,
    });
  }
  lines.push({
    text: `Members: ${data.members.count} (+${data.members.joined} / -${data.members.left})`,
    priority: 1,
  });
  lines.push({ text: UPKEEP_REMINDER, priority: Infinity });
  lines.push({ text: REGISTRATION_REMINDER, priority: Infinity });

  const join = (ls: Line[]) => ls.map((l) => l.text).join('\n');
  let kept = lines;
  while (join(kept).length > TEXT_LIMIT) {
    const lowest = Math.min(...kept.map((l) => l.priority));
    if (!Number.isFinite(lowest)) break;
    kept = kept.filter((l) => l !== kept.find((x) => x.priority === lowest));
  }
  return join(kept);
}

export default function WeeklyTab() {
  const [week, setWeek] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['insights', 'weekly', week],
    queryFn: () => api.getAdminWeeklyReport(week),
  });
  const alliance = useAllianceLabel();
  const posterRef = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const generated = useMemo(() => (q.data ? buildText(q.data, alliance) : ''), [q.data, alliance]);
  const [text, setText] = useState('');
  useEffect(() => setText(generated), [generated]);

  if (q.isLoading || !q.data) return <Loading />;
  const data = q.data;

  const runExport = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      await downloadPoster(posterRef.current, `weekly-report-${data.weekStart}.png`);
    } catch (err) {
      console.error(err);
      alert('Export failed. Check console.');
    } finally {
      setBusy(false);
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Copy failed — select the text and copy it manually.');
    }
  };

  // Kill imports usually land Sunday morning; the week's end value needs it.
  const killImportMissing = !data.kills.importNearEnd;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeek(data.prevWeek)}
            className="rounded border border-slate-700 p-1.5 text-slate-300 hover:bg-slate-800"
            aria-label="Previous week"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="min-w-[10rem] text-center text-sm font-medium tabular-nums">
            {dayMonth(data.weekStart)} – {dayMonth(data.weekEnd)}
            {!data.isComplete && <span className="ml-1 text-xs text-amber-300">(running)</span>}
          </div>
          <button
            type="button"
            onClick={() => data.nextWeek && setWeek(data.nextWeek)}
            disabled={!data.nextWeek}
            className="rounded border border-slate-700 p-1.5 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
            aria-label="Next week"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <p className="text-sm text-slate-400">Week = Sunday – Saturday, server time (VS week incl. Buster Day).</p>
      </div>

      {killImportMissing && (
        <div className="rounded border border-amber-700/60 bg-amber-950/30 p-3 text-sm text-amber-200">
          No kill import since the week ended
          {data.kills.lastImportAt && ` (last one ${fmtDate(data.kills.lastImportAt)})`} — import
          the kills first, otherwise the kill increase stays mostly empty.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <PosterCard
          title="Poster"
          description="VS, kills, CS / DS, perfect week, data upkeep, trains and members — with changes vs. last week."
          busy={busy}
          onExport={runExport}
        >
          <PosterScaledPreview>
            <div ref={posterRef}>
              <WeeklyPoster data={data} />
            </div>
          </PosterScaledPreview>
        </PosterCard>

        <div className="space-y-4">
          <Panel
            title="Chat text"
            actions={
              <button
                type="button"
                onClick={copyText}
                className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium hover:bg-indigo-500"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy text'}
              </button>
            }
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              className="w-full rounded border border-slate-700 bg-slate-950 p-2 font-mono text-xs text-slate-200"
            />
            <div className="mt-1 flex items-center justify-between text-[11px]">
              <button
                type="button"
                onClick={() => setText(generated)}
                disabled={text === generated}
                className="text-slate-400 hover:text-white disabled:opacity-30"
              >
                Reset to generated
              </button>
              <span className={cn('tabular-nums', text.length > TEXT_LIMIT ? 'text-red-300' : 'text-slate-500')}>
                {text.length} / {TEXT_LIMIT}
              </span>
            </div>
          </Panel>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <StatTile
              label="Perfect week"
              value={data.perfect.count}
              sub={`${countDelta(data.perfect.count, data.previous.perfect).text} vs last week`}
              tone="good"
            />
            <StatTile
              label="CS registered"
              value={data.cs?.registered ?? '—'}
              sub={data.cs ? `${data.cs.notPicked} not picked` : 'no CS'}
            />
            <StatTile
              label="DS registered"
              value={data.ds?.registered ?? '—'}
              sub={data.ds ? `${data.ds.notPicked} not picked` : 'no DS'}
            />
            <StatTile
              label="CS + DS unique"
              value={data.storms.uniqueRegistered}
              sub={`${data.storms.notPickedAtAll} picked for neither`}
            />
            <StatTile
              label="VS total"
              value={data.vs ? fmtBillions(data.vs.totalM) : '—'}
              sub={
                data.vs
                  ? `${data.vs.atTarget}/${data.vs.players} ≥ ${data.vs.targetM} M${vsDelta(data) ? ` · ${vsDelta(data)!.text}` : ''}`
                  : 'no VS week'
              }
            />
            <StatTile
              label="Kill increase"
              value={data.kills.measured > 0 ? `+${fmtKills(data.kills.totalM)}` : '—'}
              sub={`${data.kills.measured} measured${pctDelta(data.kills.medianM, data.previous.killsMedianM) ? ` · median ${pctDelta(data.kills.medianM, data.previous.killsMedianM)!.text}` : ''}`}
            />
            <StatTile
              label="CS attendance"
              value={fmtPct(data.cs?.attendanceRate)}
              sub={data.cs ? `${data.cs.noShow} no-show${data.cs.pending ? ` · ${data.cs.pending} open` : ''}` : 'no CS'}
              tone={data.cs?.noShow ? 'warn' : 'default'}
            />
            <StatTile
              label="DS attendance"
              value={fmtPct(data.ds?.attendanceRate)}
              sub={data.ds ? `${data.ds.noShow} no-show${data.ds.pending ? ` · ${data.ds.pending} open` : ''}` : 'no DS'}
              tone={data.ds?.noShow ? 'warn' : 'default'}
            />
            <StatTile
              label="Reported this week"
              value={`${data.reporting.reported}/${data.reporting.members}`}
              sub={`${countDelta(data.reporting.reported, data.previous.reported).text} vs last week · ${data.reporting.atRisk} at risk`}
              tone={data.reporting.atRisk + data.reporting.missing > 0 ? 'warn' : 'good'}
            />
            <StatTile
              label="Members"
              value={data.members.count}
              sub={`+${data.members.joined} / -${data.members.left}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Poster (inline styles only) ----------

const C = {
  vs: '#f59e0b',
  kills: '#f43f5e',
  cs: '#38bdf8',
  ds: '#a78bfa',
  upkeep: '#22c55e',
  muted: '#94a3b8',
};

function Box({ color, title, children, style }: { color: string; title: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        borderRadius: 16,
        border: `2px solid ${color}55`,
        background: `${color}10`,
        padding: '14px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        ...style,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Big({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{children}</div>;
}

function Sub({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 16, color: C.muted }}>{children}</div>;
}

function TopList({ rows, color }: { rows: { name: string; value: string }[]; color: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 19 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ color, fontWeight: 700, marginRight: 8 }}>#{i + 1}</span>
            {r.name}
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function PosterDelta({ d }: { d: Delta | null }) {
  if (!d) return null;
  return (
    <span
      style={{
        fontSize: 16,
        fontWeight: 600,
        color: d.up == null ? C.muted : d.up ? '#6ee7b7' : '#fcd34d',
        marginLeft: 10,
      }}
    >
      {d.text}
    </span>
  );
}

// Names on the Perfect week box, cut off with "+N more" when too long.
const PERFECT_NAMES_MAX_CHARS = 520;

function PerfectBox({ data }: { data: WeeklyReport }) {
  const names = data.perfect.players.map(playerLabel);
  const shown: string[] = [];
  let chars = 0;
  for (const n of names) {
    if (chars + n.length + 3 > PERFECT_NAMES_MAX_CHARS) break;
    shown.push(n);
    chars += n.length + 3;
  }
  const more = names.length - shown.length;
  return (
    <Box color={C.upkeep} title="Perfect week">
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <Big>{data.perfect.count}</Big>
            <PosterDelta d={countDelta(data.perfect.count, data.previous.perfect)} />
          </div>
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.35 }}>
            {data.vs ? `≥ 7.2 M on all ${data.perfect.vsDays} VS days · ` : ''}showed at every CS / DS
            picked for · reported power
          </div>
        </div>
        <div style={{ fontSize: 15, lineHeight: 1.45, color: '#e2e8f0' }}>
          {shown.length === 0 ? (
            <span style={{ color: C.muted }}>Nobody this week</span>
          ) : (
            <>
              {shown.join(' · ')}
              {more > 0 && <span style={{ color: C.muted }}> · +{more} more</span>}
            </>
          )}
        </div>
      </div>
    </Box>
  );
}

function StormBox({
  label,
  color,
  s,
  prevRate,
}: {
  label: string;
  color: string;
  s: WeeklyStorm | null;
  prevRate: number | null;
}) {
  return (
    <Box color={color} title={label} style={{ flex: 1 }}>
      {!s || s.picked === 0 ? (
        <Sub>No event this week</Sub>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <Big>{fmtPct(s.attendanceRate)}</Big>
            <PosterDelta d={ptsDelta(s.attendanceRate, prevRate)} />
          </div>
          <Sub>attendance · {dayMonth(s.date)}</Sub>
          <div style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
            {s.registered} registered · {s.starters} + {s.subs} picked · {s.notPicked} not picked
          </div>
          <div style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ color: '#6ee7b7' }}>{s.showed} showed</span>
            {s.late > 0 && <span style={{ color: '#fcd34d' }}> · {s.late} late</span>}
            <span style={{ color: s.noShow > 0 ? '#fca5a5' : C.muted }}> · {s.noShow} no-show</span>
          </div>
        </>
      )}
    </Box>
  );
}

function WeeklyPoster({ data }: { data: WeeklyReport }) {
  const allianceLabel = useAllianceLabel();
  const vs = data.vs;
  const dayMax = Math.max(...(vs?.dayTotalsM ?? []).map((v) => v ?? 0), 1);
  return (
    <PosterFrame
      accent={C.vs}
      eyebrow="Weekly report"
      title={`${allianceLabel} · Week in numbers`}
      weekLabel={`${dayMonth(data.weekStart)} – ${dayMonth(data.weekEnd)}`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
        <Box color={C.vs} title={vs ? `VS · ${KIND_LABEL[vs.kind]} vs ${vs.opponentName} ${vs.opponentServer}` : 'VS'}>
          {!vs ? (
            <Sub>No VS week recorded</Sub>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
                  <Big>{fmtBillions(vs.totalM)}</Big>
                  <PosterDelta d={vsDelta(data)} />
                  {vs.outcome && (
                    <span
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: vs.outcome === 'win' ? '#6ee7b7' : '#fca5a5',
                      }}
                    >
                      {vs.outcome === 'win' ? 'Won' : 'Lost'}
                    </span>
                  )}
                </div>
                <Sub>
                  {vs.atTarget} of {vs.players} players ≥ {vs.targetM} M
                </Sub>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 110, marginTop: 6 }}>
                  {vs.dayTotalsM.map((v, i) => (
                    <div
                      key={i}
                      style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 3 }}
                    >
                      <div style={{ fontSize: 12, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
                        {v == null ? '' : v.toFixed(0)}
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: `${v == null ? 0 : Math.max(4, (v / dayMax) * 70)}%`,
                          background: C.vs,
                          borderRadius: 5,
                          opacity: 0.85,
                        }}
                      />
                      <div style={{ fontSize: 12, color: C.muted }}>{DAY_LABELS[i]}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Sub>{vs.topMode === 'save' ? 'Save masters · closest to 7.2 M/day (Mon–Thu)' : 'Top players'}</Sub>
                {vs.top.length === 0 ? (
                  <Sub>Nobody hit 7.2 M on every day Mon–Thu</Sub>
                ) : (
                  <TopList
                    color={C.vs}
                    rows={vs.top.map((t) => ({
                      name: playerLabel(t.player),
                      value: t.overM != null ? `+${t.overM.toFixed(1)} M` : `${t.scoreM} M`,
                    }))}
                  />
                )}
              </div>
            </div>
          )}
        </Box>

        <Box color={C.kills} title="Kill increase">
          {data.kills.measured === 0 ? (
            <Sub>No kill reports at both ends of the week</Sub>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Big>+{fmtKills(data.kills.totalM)}</Big>
                <Sub>
                  {data.kills.measured} players measured · median +{fmtKills(data.kills.medianM ?? 0)}
                  <PosterDelta d={pctDelta(data.kills.medianM, data.previous.killsMedianM)} />
                </Sub>
              </div>
              <TopList
                color={C.kills}
                rows={data.kills.top.map((t) => ({ name: playerLabel(t.player), value: `+${fmtKills(t.deltaKillsM)}` }))}
              />
            </div>
          )}
        </Box>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <StormBox label="Canyon Storm" color={C.cs} s={data.cs} prevRate={data.previous.csRate} />
            <StormBox label="Desert Storm" color={C.ds} s={data.ds} prevRate={data.previous.dsRate} />
          </div>
          {data.storms.uniqueRegistered > 0 && (
            <div style={{ fontSize: 17, color: '#cbd5e1', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
              CS + DS: <b>{data.storms.uniqueRegistered}</b> unique players registered ·{' '}
              <b>{data.storms.notPickedAtAll}</b> picked for neither
            </div>
          )}
        </div>

        <PerfectBox data={data} />

        <div style={{ display: 'flex', gap: 16 }}>
          <Box color={C.upkeep} title="Data upkeep" style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <Big>
                {data.reporting.reported}/{data.reporting.members}
              </Big>
              <PosterDelta d={countDelta(data.reporting.reported, data.previous.reported)} />
            </div>
            <Sub>reported power this week</Sub>
          </Box>
          <Box color={C.muted} title="Trains" style={{ flex: 1 }}>
            <Big>{data.trains.regular}</Big>
            <Sub>{data.trains.megaExpress > 0 ? `+ ${data.trains.megaExpress} Mega Express` : 'regular trains'}</Sub>
          </Box>
          <Box color={C.muted} title="Members" style={{ flex: 1 }}>
            <Big>{data.members.count}</Big>
            <Sub>
              +{data.members.joined} joined · -{data.members.left} left
            </Sub>
          </Box>
        </div>

        <div
          style={{
            marginTop: 'auto',
            borderRadius: 16,
            background: `${C.vs}22`,
            border: `2px solid ${C.vs}77`,
            padding: '14px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: 19,
          }}
        >
          <div>
            <span style={{ color: C.vs, fontWeight: 700 }}>Keep your data up to date: </span>
            report power and kills in the Player Hub at least every 2 weeks.
          </div>
          <div>
            <span style={{ color: C.vs, fontWeight: 700 }}>Registration: </span>
            CS by Monday 12:00 ST · DS by Wednesday 12:00 ST
          </div>
        </div>
      </div>
    </PosterFrame>
  );
}
